import React, { useState, useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';

function MapView({ listings, height = '400px', onMarkerClick }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  // Initialize map when component mounts
  useEffect(() => {
    // Dynamic import for Leaflet to avoid SSR issues
    import('leaflet').then(L => {
      // Only initialize map once
      if (mapInstanceRef.current) return;

      // Create map instance
      const map = L.map(mapRef.current, {
        center: [-1.2921, 36.8219], // Default to Nairobi
        zoom: 12,
        scrollWheelZoom: false,
      });

      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Store map instance for later reference
      mapInstanceRef.current = map;
      setIsMapInitialized(true);

      // Clean up on unmount
      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
      };
    });
  }, []);

  // Update markers when listings change
  useEffect(() => {
    if (!isMapInitialized || !listings?.length) return;

    // Dynamic import for Leaflet
    import('leaflet').then(L => {
      // Clear existing markers
      if (markersRef.current.length) {
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
      }

      // Parse listings for mapping
      const validListings = listings.filter(listing => {
        // Check if listing has valid location data
        const hasValidLocation = 
          (listing.location?.lat && listing.location?.lng) ||
          (listing.latitude && listing.longitude);
        
        if (!hasValidLocation) {
          console.log('Listing without valid location data:', listing.id || listing._id, listing.title);
        }
        
        return hasValidLocation;
      });

      // If no valid listings, return
      if (!validListings.length) return;

      // Create icon for markers
      const foodIcon = L.divIcon({
        html: `<div class="bg-primary-500 rounded-full p-1 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
               </div>`,
        className: 'custom-div-icon',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });

      // Add markers for each listing
      validListings.forEach(listing => {
        const marker = L.marker([listing.location.lat, listing.location.lng], {
          icon: foodIcon,
          title: listing.title
        }).addTo(mapInstanceRef.current);

        // Format expiry date with days remaining indicator
        const expiryDate = new Date(listing.expiryDate);
        const today = new Date();
        const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        const expiryText = daysRemaining <= 0 ? 'Expired' : 
                         daysRemaining === 1 ? 'Expires today!' : 
                         daysRemaining <= 3 ? `Expires in ${daysRemaining} days!` : 
                         `Expires in ${daysRemaining} days`;
        const expiryClass = daysRemaining <= 0 ? 'crimson' : 
                           daysRemaining <= 1 ? 'red' : 
                           daysRemaining <= 3 ? 'orangered' : 'green';
        
        // Clean up quantity
        const quantity = listing.quantity ? 
          `${listing.quantity} ${listing.unit || ''}`.trim() : 
          'Available';
          
        // Get listing image or placeholder
        const imageUrl = listing.imageUrl || listing.image || '/images/placeholder-food.png';
        
        // Format location/address
        const addressParts = [];
        if (listing.organization) addressParts.push(listing.organization);
        if (listing.address) addressParts.push(listing.address);
        if (listing.city) addressParts.push(listing.city);
        const locationText = addressParts.length > 0 ? addressParts.join(', ') : 'Location available on detail page';
        
        // Add popup with enhanced listing info
        marker.bindPopup(`
          <div style="min-width: 280px; padding: 12px; font-family: sans-serif;">
            <div style="position: relative; margin-bottom: 10px; border-radius: 4px; overflow: hidden;">
              <div style="height: 100px; overflow: hidden; background: #f3f4f6; position: relative;">
                <img 
                  src="${imageUrl}" 
                  onerror="this.onerror=null; this.src='/images/placeholder-food.png';"
                  style="width: 100%; object-fit: cover; min-height: 100%;" 
                  alt="${listing.title}"
                />
              </div>
              <div style="position: absolute; top: 8px; right: 8px; background-color: ${expiryClass === 'green' ? '#10B981' : '#EF4444'}; color: white; font-size: 12px; font-weight: 500; padding: 4px 8px; border-radius: 4px;">
                ${expiryText}
              </div>
            </div>
            
            <h3 style="font-weight: 600; font-size: 18px; margin: 0 0 8px 0; color: #1F2937;">${listing.title}</h3>
            
            <div style="display: flex; margin-bottom: 8px; align-items: center;">
              <span style="display: inline-block; background-color: #E5E7EB; color: #4B5563; font-size: 12px; font-weight: 500; padding: 2px 8px; border-radius: 12px; margin-right: 8px;">
                ${listing.foodType || 'Food'}
              </span>
              <span style="color: #6B7280; font-size: 14px;">${quantity}</span>
            </div>
            
            <p style="margin: 8px 0; font-size: 13px; color: #6B7280; display: flex; align-items: center;">
              <svg xmlns="http://www.w3.org/2000/svg" style="width: 14px; height: 14px; margin-right: 5px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              ${locationText}
            </p>
            
            <div style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center;">
              <p style="margin: 0; font-size: 13px; color: #6B7280;">
                <span style="display: inline-block; background-color: #F3F4F6; padding: 3px 8px; border-radius: 4px;">
                  ${new Date(listing.expiryDate).toLocaleDateString()}
                </span>
              </p>
              <a href="/listings/${listing.id}" style="display: inline-block; background-color: #10B981; color: white; padding: 6px 12px; border-radius: 4px; text-decoration: none; font-size: 14px; font-weight: 500;">
                View Details
              </a>
            </div>
          </div>
        `, { 
          minWidth: 280,
          maxWidth: 320,
          className: 'listing-popup' 
        });
        
        // Add click handler if provided
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(listing));
        }

        // Store marker reference
        markersRef.current.push(marker);
      });

      // Fit map bounds to show all markers
      if (markersRef.current.length) {
        const group = L.featureGroup(markersRef.current);
        mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
      }
    });
  }, [listings, isMapInitialized, onMarkerClick]);

  return (
    <div className="rounded-lg overflow-hidden shadow-md border border-gray-200">
      <div 
        ref={mapRef} 
        style={{ height }} 
        className="w-full"
      >
        {!listings?.length && (
          <div className="h-full w-full flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">No location data available</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MapView;
